import type { PopperPlacementType, PopperProps, Theme } from '@material-ui/core'
import { ClickAwayListener, FormControl, Popper, makeStyles } from '@material-ui/core'
import type { Variant as TypographyVariant } from '@material-ui/core/styles/createTypography'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ClearIcon from '@material-ui/icons/Clear'
import { useSnackbar } from 'notistack'
import type { ReactNode } from 'react'
import React, { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from '@material-ui/core/styles/withStyles'
import classNames from 'classnames'
function slugify(text: string): string {
  if (!text) return ''
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '')
}

const USER_COLOR_PALETTE = [
  '#4E7FD0',
  '#4E943E',
  '#C06325',
  '#8070CA',
  '#088CB9',
  '#139676',
  '#BA5B8B',
  '#868604',
  '#0D9298',
  '#CE5057',
  '#AD721C',
  '#A363B2'
]
import Collapse from '../Collapse'
import Skeleton from '../Skeleton'
import { useIsMobile } from '../../hooks'
import Grid from '../Grid'
import type { InputFieldProps } from '../InputField'
import InputField from '../InputField'
import InputLabel from '../InputLabel'
import Paper from '../Paper'
import Tooltip from '../Tooltip'
import Typography from '../Typography'
import Spinner from '../Spinner'
import Avatar from '../Avatar'
import DefaultDropdownSelectMenuItem from './DefaultDropdownSelectMenuItem'
import type { DropdownSelectMenuItemProps } from './DefaultDropdownSelectMenuItem'
import DropdownSelectFooter from './DropdownSelectFooter'
import DropdownSelectSearchBar from './DropdownSelectSearchBar'
import DropdownChips from './DropdownChips'

const useStyles = makeStyles<Theme, DropdownSelectProps>((theme: Theme) => ({
  root: {},
  inputLabel: {
    marginBottom: '5px'
  },

  tooltipTypography: {
    fontSize: '0.75rem',
    fontWeight: '500',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.65rem'
    }
  },
  toolTipLabel: {
    fontSize: '0.7rem',
    fontWeight: '500',
    marginBottom: '5px'
  },
  toolTipWrapper: {
    display: 'flex',
    alignContent: 'center',
    justifyContent: 'flex-start',
    paddingBottom: theme.spacing(0.5),
    marginTop: `-${theme.spacing(0.5)}px`
  },
  dealViewContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginLeft: theme.spacing(1)
  },
  menuItemsContainer: {
    maxHeight: ({ maxMenuHeight }) => `${maxMenuHeight ?? '300'}px`,
    overflow: 'auto',
    paddingBottom: theme.spacing(1),
    scrollbarWidth: 'thin',
    '&::-webkit-scrollbar': {
      width: 6
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.paintscout.palette.brand.Neutral.P80,
      borderRadius: 3
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent'
    }
  },
  noOptionsMessage: {
    padding: theme.spacing(2),
    color: theme.palette.text.disabled
  },
  icon: {
    cursor: ({ disabled }) => (disabled ? 'default' : 'pointer'),
    color: theme.paintscout.palette.brand.Neutral.P80,
    marginRight: -theme.spacing(1),
    fontSize: ({ shrink }) => (shrink ? '1.25rem' : '1.5rem'),
    width: ({ shrink }) => (shrink ? '24px' : '36px')
  },
  ownedByViewIcon: {
    borderRadius: '50%',
    height: theme.spacing(2),
    width: theme.spacing(2),
    cursor: ({ disabled }) => (disabled ? 'default' : 'pointer'),
    color: theme.paintscout.palette.brand.Neutral.P80,
    marginLeft: theme.spacing(0.5),
    '&:hover': {
      color: ({ disabled }) =>
        disabled ? theme.paintscout.palette.brand.Neutral.P80 : theme.paintscout.palette.brand.Neutral.P35,
      backgroundColor: ({ disabled }) =>
        disabled ? theme.paintscout.palette.brand.Neutral.P94 : theme.paintscout.palette.brand.Neutral.P80
    }
  },
  selectInputRoot: ({ nestedDropdown, typographyVariant, headerTypographyVariant }) => ({
    ...theme.typography[headerTypographyVariant || typographyVariant],
    margin: 0,
    ...(nestedDropdown
      ? {
          border: 'none !important',
          backgroundColor: 'transparent'
        }
      : {}),
    boxShadow: nestedDropdown ? 'none !important' : undefined,
    caretColor: 'transparent',
    // Safari mobile: skip the synthetic 300ms tap-delay so the dropdown opens
    // on the first tap. The trigger is a readOnly input (see #20209), which
    // iOS treats with extra heuristics — manipulation makes the tap behave
    // like a plain click.
    touchAction: 'manipulation'
  }),
  selectInput: {
    borderRadius: theme.borderRadius.md,
    cursor: ({ disabled }) => (disabled ? 'default' : 'pointer'),
    paddingRight: 0,
    textTransform: ({ inputTextTransform }) => inputTextTransform || 'none',
    color: ({ inputTextColor, disabled }) =>
      inputTextColor || (disabled ? theme.palette.text.disabled : theme.palette.text.primary),
    padding: ({ shrink }) => (shrink ? theme.spacing(0.25, 1) : undefined)
  },
  openSelectInput: {
    borderRadius: theme.borderRadius.md,
    borderColor: theme.paintscout.palette.primary + ' !important',
    boxShadow: `0 0 0 1px ${theme.paintscout.palette.primary} !important`
  },
  popper: {
    zIndex: theme.zIndex.modal + 1,
    boxShadow: theme.shadows[4],
    marginTop: '1px'
  },
  sublabel: {
    ...theme.typography.caption,
    color: theme.paintscout.palette.brand.Neutral.P70
  },
  paper: {
    paddingTop: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    boxShadow: 'none',
    position: 'relative',
    // Safari mobile: skip the 300ms double-tap-zoom delay on every option
    // inside the popper. Without this, the first tap on an option can be
    // swallowed when a parent form does any focus/layout work mid-tap.
    touchAction: 'manipulation'
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1),
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 1
  },
  closeButton: {
    padding: theme.spacing(0.5),
    cursor: 'pointer',
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.text.primary
    }
  },
  groupLabel: {
    padding: theme.spacing(1, 2),
    backgroundColor: theme.paintscout.palette.brand.Neutral.P96
  },
  // This is a hack to get the helper text to display above the input.
  // We can use it to apply formatOptionLabel to the selected value.
  helperText: {
    position: 'absolute',
    top: '10%',
    left: 2,
    width: 'calc(100% - 38px)',
    height: '80%',
    backgroundColor: ({ disabled }) =>
      disabled ? theme.paintscout.palette.brand.Neutral.P94 : theme.palette.background.paper,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(1),
    // allow clicks to pass through to the input
    pointerEvents: 'none'
  },
  hiddenFocus: {
    border: 'none',
    boxShadow: 'none'
  },
  adornment: {
    marginLeft: 0
  },
  ownedByViewAdornment: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    padding: theme.spacing(0.5),
    borderRadius: theme.borderRadius.md,
    '& svg': {
      color: theme.paintscout.palette.brand.Neutral.P50,
      '&:hover': {
        color: theme.paintscout.palette.brand.Neutral.P50
      }
    },
    '&:hover': {
      color: theme.palette.primary.main,
      '& svg': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P94,
        color: theme.paintscout.palette.brand.Neutral.P50
      }
    }
  },
  profileImage: {
    borderRadius: '50%',
    objectFit: 'cover',
    fontSize: '1.5em',
    width: '1.2em',
    height: '1.2em',
    margin: theme.spacing(),
    marginLeft: 0
  },
  fillerIcon: {
    margin: 0,
    padding: 0,
    marginRight: `-${theme.spacing(0.5)}px`
  },
  ownedDropdown: {
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      cursor: ({ disabled }) => (disabled ? 'default' : 'pointer'),
      color: theme.palette.primary.main,
      '& svg': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P94,
        color: theme.paintscout.palette.brand.Neutral.P50
      }
    }
  }
}))

export interface DropdownSelectPlaceholder {
  selectPlaceholder?: string // Select component placeholder
  plural?: string // Used in the dropdown's search bar
}

export interface DropdownSelectOption {
  key?: string
  label: string
  value: any
  sublabel?: string
  sublabelMuted?: boolean
  dotColor?: string
  disabled?: boolean
  forceTop?: boolean
  picture?: string
  tooltip?: string
  [key: string]: any
}

export interface GroupedDropdownSelectOption {
  label: string
  options: DropdownSelectOption[]
}

interface BaseDropdownSelectProps extends Omit<InputFieldProps, 'onChange' | 'placeholder' | 'helperText'> {
  label?: string
  options?: DropdownSelectOption[] | GroupedDropdownSelectOption[]
  autoApply?: boolean
  placeholder?: DropdownSelectPlaceholder
  loading?: boolean
  toolTip?: string | ReactNode
  disabled?: boolean
  searchable?: boolean
  searchCallback?: (queryText: string) => void
  noOptionsMessage?: string
  hideIcon?: boolean
  popperPlacement?: PopperPlacementType
  popperModifiers?: PopperProps['modifiers']
  disablePortal?: boolean
  nestedDropdown?: boolean // For components like the Discount Rate Dropdown
  nestedDropdownBorder?: boolean
  maxMenuHeight?: number
  typographyVariant?: TypographyVariant
  headerTypographyVariant?: TypographyVariant
  textWidth?: number // The width to make text for cutoff in dropdown input
  inputTextTransform?: CSSProperties['textTransform']
  inputTextColor?: CSSProperties['color']
  shrink?: boolean
  pagination?: {
    currentPage: number
    pages: ReactNode[]
    onPageChange?: (page: number) => void
  }
  minimumSearchCharacters?: number
  hideSelectAll?: boolean
  showCollapsibleChips?: boolean
  autoChipsOpen?: boolean
  trimLabel?: { length: number; ellipsis?: boolean }
  ownedByView?: boolean
  dealView?: boolean
  customOwnedByLabel?: ReactNode
  denseOptions?: boolean
  onMinimumSearchCharactersReached?: (searchFilter: string) => void
  validateCreatedOption?: (option: string) => string
  checkInvalid?: () => string
  createLabel?: (val: string) => string
  formatOptionLabel?: (option: DropdownSelectOption, placement?: 'menu-item' | 'selected') => React.ReactNode
  onCreateOption?: (option: string) => void
  createAtTop?: boolean
  hasUnselect?: boolean
  hideFooter?: boolean
  hideCloseButton?: boolean
  hideDoneButton?: boolean
  renderMenuItem?: React.ComponentType<DropdownSelectMenuItemProps>
}

export interface SingleSelectProps extends BaseDropdownSelectProps {
  variant: 'single'
  value: DropdownSelectOption
  onChange: (option: DropdownSelectOption) => void
}

export interface MultiSelectProps extends BaseDropdownSelectProps {
  variant: 'multi'
  value: DropdownSelectOption[]
  onChange: (options: DropdownSelectOption[]) => void
}

export type DropdownSelectProps = SingleSelectProps | MultiSelectProps

export default function DropdownSelect(props: DropdownSelectProps) {
  const classes = useStyles(props)

  const {
    label,
    placeholder,
    toolTip,
    disabled,
    hideIcon,
    variant,
    loading,
    autoApply = true,
    noOptionsMessage = 'No options',
    nestedDropdown,
    typographyVariant = 'body1',
    hideSelectAll,
    formatOptionLabel,
    createLabel,
    validateCreatedOption,
    checkInvalid = () => '',
    onChange: onChangeProp,
    onCreateOption,
    onMinimumSearchCharactersReached,
    popperModifiers,
    disablePortal = true,
    pagination,
    popperPlacement = 'bottom-start',
    sublabel,
    minimumSearchCharacters,
    searchable: propsSearchable,
    searchCallback,
    showCollapsibleChips,
    autoChipsOpen,
    trimLabel,
    ownedByView,
    dealView,
    customOwnedByLabel,
    denseOptions,
    createAtTop,
    hasUnselect,
    hideFooter,
    hideCloseButton = true,
    hideDoneButton = false,
    renderMenuItem: MenuItemComponent = DefaultDropdownSelectMenuItem,
    ...inputProps
  } = props

  const multi = variant === 'multi'
  const searchable = propsSearchable || !!minimumSearchCharacters

  const [anchorEl, setAnchorEl] = useState(null)
  const selectRef = useRef<HTMLInputElement>(null)
  const [searchFilter, setSearchFilter] = useState('')
  const [allSelected, setAllSelected] = useState(false)
  const [hasDeletedChips, setHasDeletedChips] = useState(false)
  const mobile = useIsMobile()
  const { enqueueSnackbar } = useSnackbar()
  const [intermediateValue, setIntermediateValue] = useState<DropdownSelectProps['value']>(
    props.value ?? (multi ? [] : null)
  )

  // Track the width of the select input to determine if the selected options are overflowing
  // Must be done in state via useEffect to trigger re-renders as refs don't cause re-renders
  const [selectWidth, setSelectWidth] = useState<number | null>(null)
  useEffect(() => {
    setSelectWidth(selectRef.current?.clientWidth || null)
  }, [selectRef.current?.clientWidth])

  const ownedByUrl =
    !multi &&
    (props?.value?.customAvatar ? props?.value?.customAvatar : props?.value?.picture ? props?.value?.picture : '')
  const ownedByName = !multi && props?.value?.label ? props?.value?.label : ''
  const ownedByColor = !multi && props?.value?.color ? props?.value?.color : USER_COLOR_PALETTE[0]

  useEffect(() => {
    if (searchCallback) {
      searchCallback(searchFilter)
    }
  }, [searchFilter])

  const optionsAreGrouped = (
    options: DropdownSelectOption[] | GroupedDropdownSelectOption[]
  ): options is GroupedDropdownSelectOption[] => {
    const groupedOptions = (options as GroupedDropdownSelectOption[]) || []
    return groupedOptions.some((option) => optionIsGrouped(option))
  }

  const optionIsGrouped = (
    option: DropdownSelectOption | GroupedDropdownSelectOption
  ): option is GroupedDropdownSelectOption => {
    return (option as GroupedDropdownSelectOption).options !== undefined
  }

  const value = autoApply ? props.value ?? (multi ? [] : null) : intermediateValue

  const calculatedOptions =
    multi && !optionsAreGrouped(props.options)
      ? [
          ...props.options.filter((option) => option.forceTop),
          ...props.value.filter((selectedOption) => !selectedOption.forceTop),
          ...props.options.filter(
            (option) =>
              !(props.value as DropdownSelectOption[]).some((selectedOption) =>
                selectedOption.key ? selectedOption.key === option.key : selectedOption.value === option.value
              ) && !option.forceTop
          )
        ]
      : props.options

  const [options, setOptions] = useState<DropdownSelectProps['options']>(calculatedOptions)

  // whenever the dropdown is opened, reset the options to the calculated options
  useEffect(() => {
    setOptions(calculatedOptions)
  }, [anchorEl, intermediateValue])

  // keep options in sync with calculated options when an option is created
  useEffect(() => {
    if (calculatedOptions?.length > options?.length) {
      setOptions(calculatedOptions)
    }
  }, [calculatedOptions, options])

  // keep options in sync with calculated options when an option is disabled
  useEffect(() => {
    if (!optionsAreGrouped(calculatedOptions) && !optionsAreGrouped(options)) {
      const optionsNeedToGetDisabled = calculatedOptions.some((calculatedOptions) => {
        const matchingOption = options.find((o) =>
          o.key ? o.key === calculatedOptions.key : o.value === calculatedOptions.value
        )
        return matchingOption && calculatedOptions.disabled !== matchingOption.disabled
      })

      if (optionsNeedToGetDisabled) {
        const calculatedOptionsOrderedByOptions = options.map((option) => {
          const matchingOption = calculatedOptions.find((o) =>
            o.key ? o.key === option.key : o.value === option.value
          )
          return matchingOption || option
        })

        setOptions(calculatedOptionsOrderedByOptions)
      }
    }
  }, [calculatedOptions, options])

  useEffect(() => {
    if (!autoApply) {
      setIntermediateValue(props.value ?? (multi ? [] : null))
      if (!props?.value?.length && allSelected) {
        setAllSelected(false)
      }
    }
  }, [props.value])

  // If we're async the options are changing, so keep them in sync
  useEffect(() => {
    setOptions(calculatedOptions)
  }, [props.options])

  // Keep "Select All" state aligned with the current value.
  useEffect(() => {
    if (!multi) {
      return
    }
    const allOptionsSelected =
      options && value && options.length > 1 && options.length === (value as DropdownSelectOption[]).length
    if (allSelected !== allOptionsSelected) {
      setAllSelected(allOptionsSelected)
    }
  }, [options, value, multi, allSelected])

  // If the anchor element changes, we need to call onFocus and onBlur
  useEffect(() => {
    if (anchorEl && typeof props.onFocus === 'function') {
      props.onFocus({ target: selectRef.current } as any)
    }
    if (!anchorEl && typeof props.onBlur === 'function') {
      props.onBlur({ target: selectRef.current } as any)
    }
  }, [anchorEl])

  const getReadableValue = () => {
    if (multi) {
      return (value as DropdownSelectOption[]).map((option) => option.label).join(', ')
    } else {
      return (value as DropdownSelectOption)?.label || ''
    }
  }

  const selectedOptionsAreOverflowing = () => {
    if (!selectWidth) {
      return false
    }
    const readableValue = getReadableValue()
    const textWidth = readableValue.length * (props?.textWidth || 8.25) // 8px ish per character plus a buffer
    return textWidth > selectWidth
  }

  const getDisplayValue = () => {
    // If all options are selected, display 'All Selected'
    // extra check for multi select to ensure that the display value doesn't flicker when all options are selected
    const allOptionsSelected =
      options && value && options.length > 1 && options.length === (value as DropdownSelectOption[]).length
    if (multi && !minimumSearchCharacters && (allSelected || allOptionsSelected)) {
      return 'All Selected'
    } else if (multi && selectedOptionsAreOverflowing() && (value as DropdownSelectOption[]).length > 1) {
      return `${(value as DropdownSelectOption[]).length} Selected`
    } else {
      if (selectedOptionsAreOverflowing() && trimLabel) {
        return `${getReadableValue().substring(0, trimLabel.length)}${trimLabel.ellipsis ? '...' : ''}`
      }
      return getReadableValue()
    }
  }

  const optionIsSelected = (option: DropdownSelectOption) => {
    const compareValue = option.key ? 'key' : 'value'
    if (multi) {
      return !!(value as DropdownSelectOption[]).find(
        (selectedOption) => selectedOption[compareValue] === option[compareValue]
      )
    } else {
      return (value as DropdownSelectOption)?.[compareValue] === option[compareValue]
    }
  }

  const isMultiSelectHandler = (onChangeProp: any): onChangeProp is (options: DropdownSelectOption[]) => void => multi

  const isSingleSelectHandler = (onChangeProp: any): onChangeProp is (option: DropdownSelectOption) => void => !multi

  const filterOptions = () => {
    if (minimumSearchCharacters && searchFilter.length < minimumSearchCharacters) {
      return []
    }
    return options.filter((option) => {
      if (optionIsGrouped(option)) {
        const subOptionsSearch = (option as GroupedDropdownSelectOption).options.filter((subOption) =>
          subOption?.label?.toLowerCase().includes(searchFilter?.toLowerCase())
        )
        return subOptionsSearch.length > 0 ? { ...option, options: subOptionsSearch } : false
      } else {
        return option?.label?.toLowerCase().includes(searchFilter?.toLowerCase())
      }
    })
  }

  const onChange = (options: DropdownSelectOption[] | DropdownSelectOption) => {
    if (isMultiSelectHandler(onChangeProp)) {
      onChangeProp(options as DropdownSelectOption[])
    } else if (isSingleSelectHandler(onChangeProp)) {
      onChangeProp((Array.isArray(options) ? options[0] : options) as DropdownSelectOption)
    }
  }

  const handleChange = (options) => {
    if (autoApply) {
      onChange(options)
    } else {
      setIntermediateValue(options)
    }
  }

  const handleChipDelete = (option: DropdownSelectOption) => {
    setHasDeletedChips(true)
    const newOptions = (value as DropdownSelectOption[]).filter((selectedOption) =>
      selectedOption.key ? selectedOption.key !== option.key : selectedOption.value !== option.value
    )
    onChange(newOptions)
    console.log('deleting chip')
    setAllSelected(false)
  }

  const handleAllOptionClick = () => {
    if (allSelected) {
      handleChange([])
      setAllSelected(false)
    } else {
      handleChange([...options])
      setAllSelected(true)
    }
  }

  const handleOptionClick = (option) => {
    if (multi) {
      if (optionIsSelected(option)) {
        setAllSelected(false)
        handleChange(
          (value as DropdownSelectOption[]).filter((selectedOption) =>
            selectedOption.key ? selectedOption.key !== option.key : selectedOption.value !== option.value
          )
        )
      } else {
        handleChange([...(value as DropdownSelectOption[]), option])
      }
    } else {
      handleChange([option])
      if (!pagination) {
        handleApply({ allowCreateOption: false })
      }
    }
  }

  const handleApply = ({ allowCreateOption }: { allowCreateOption: boolean }) => {
    const invalidReason = checkInvalid()
    if (invalidReason.length) {
      enqueueSnackbar(invalidReason, { variant: 'error' })
      return
    }

    if (onCreateOption && searchable && !!searchFilter.trim() && allowCreateOption) {
      const invalidMessage = validateCreatedOption?.(searchFilter)
      if (invalidMessage) {
        enqueueSnackbar(invalidMessage, { variant: 'error' })
        return
      }
      onCreateOption(searchFilter)
    }
    if (!autoApply) {
      onChange(intermediateValue)
    }

    setAnchorEl(null)
    clearSearchFilter()
  }

  const emptyValue = multi ? ([] as DropdownSelectOption[]) : (null as DropdownSelectOption | null)

  const handleClear = () => {
    onChange(emptyValue as DropdownSelectProps['value'])
    setIntermediateValue(emptyValue)
    setAllSelected(false)
    if (notOnDefaultPage && !!pagination?.onPageChange) {
      pagination.onPageChange(pagination.currentPage - 1)
    } else {
      clearSearchFilter()
    }
  }

  const onCreateClick = () => {
    if (!onCreateOption) {
      return
    }

    const invalidMessage = validateCreatedOption?.(searchFilter)
    if (invalidMessage) {
      enqueueSnackbar(invalidMessage, { variant: 'error' })
      return
    }

    if (optionsAreGrouped(options)) {
      setOptions([{ label: searchFilter, value: searchFilter } as any, ...options])
    }

    onCreateOption(searchFilter)
    setSearchFilter('')
    if (autoApply && !multi) {
      setAnchorEl(null)
    }
  }

  const handleSearchFilterChange = (newSearchFilter: string) => {
    setSearchFilter(newSearchFilter)
    if (
      newSearchFilter.length >= minimumSearchCharacters &&
      !!minimumSearchCharacters &&
      !!onMinimumSearchCharactersReached
    ) {
      onMinimumSearchCharactersReached(newSearchFilter)
    }
  }

  const clearSearchFilter = () => {
    setTimeout(() => setSearchFilter(''), 200) // Delay to allow the popover to close before clearing the search filter
  }

  const handleClickAway = () => {
    if (anchorEl) {
      handleApply({ allowCreateOption: false })
    }
  }

  const handleUnselect = (ev) => {
    ev.stopPropagation()
    onChange(emptyValue as DropdownSelectProps['value'])
  }

  const hasValue = multi ? (value as DropdownSelectOption[])?.length > 0 : !!(value as DropdownSelectOption)?.value

  const notOnDefaultPage = !!pagination && pagination.pages.length && pagination.currentPage !== 0
  const shouldShowHeaderActions = !hideCloseButton
  const shouldShowFooter = !hideFooter && ((multi && hasValue) || (!multi && !autoApply))

  const CreateOption = () => (
    <Grid key={'create-option'} item xs={12}>
      <MenuItemComponent
        typographyVariant={typographyVariant}
        searchable={searchable}
        option={{
          label: createLabel ? createLabel(searchFilter) : `Create "${searchFilter}"`,
          value: searchFilter
        }}
        selected={false}
        showSelectableIcon
        onClick={onCreateClick}
      />
    </Grid>
  )

  const MenuContent = () => {
    if (notOnDefaultPage) {
      return pagination.pages[pagination.currentPage - 1]
    }

    const filteredOptions = filterOptions()
    const showCreateableOption = !!onCreateOption && searchable && !!searchFilter.trim()

    return (
      <>
        {multi && filteredOptions.length > 1 && !searchFilter && !hideSelectAll && (
          <Grid key={`select-all`} item xs={12}>
            <MenuItemComponent
              bold
              typographyVariant={typographyVariant}
              searchable={searchable}
              option={{ label: 'Select All', value: 'select-all' }}
              showSelectableIcon={multi}
              selected={allSelected}
              onClick={handleAllOptionClick}
            />
          </Grid>
        )}
        {showCreateableOption && createAtTop && <CreateOption />}
        {filteredOptions.length > 0 &&
          filteredOptions.map((option: GroupedDropdownSelectOption | DropdownSelectOption, index) => {
            if (optionIsGrouped(option)) {
              return (
                <Grid key={`${option.label}-${index}`} item xs={12}>
                  <Typography variant="h6" className={classes.groupLabel}>
                    {option.label}
                  </Typography>
                  {(option as GroupedDropdownSelectOption).options.map((option, groupedIndex) => (
                    <MenuItemComponent
                      dense={denseOptions}
                      typographyVariant={typographyVariant}
                      searchable={searchable}
                      key={`${option.key ?? option.value}-${index}-${groupedIndex}`}
                      option={option}
                      showSelectableIcon={multi}
                      formatOptionLabel={
                        formatOptionLabel ? (option) => formatOptionLabel?.(option, 'menu-item') || option.label : null
                      }
                      selected={multi && optionIsSelected(option)}
                      onClick={() => handleOptionClick(option)}
                    />
                  ))}
                </Grid>
              )
            } else {
              const unGroupedOption = option as DropdownSelectOption
              return (
                <Grid
                  className={label ? `${slugify(label)}-option-${index}` : ''}
                  key={`${unGroupedOption.value}-${index}`}
                  item
                  xs={12}
                >
                  <MenuItemComponent
                    dense={denseOptions}
                    searchable={searchable}
                    typographyVariant={typographyVariant}
                    option={unGroupedOption}
                    formatOptionLabel={
                      formatOptionLabel ? (option) => formatOptionLabel?.(option, 'menu-item') || option.label : null
                    }
                    selected={multi && optionIsSelected(unGroupedOption)}
                    showSelectableIcon={multi}
                    onClick={() => handleOptionClick(unGroupedOption)}
                  />
                </Grid>
              )
            }
          })}
        {showCreateableOption && !createAtTop && <CreateOption />}
        {!showCreateableOption && !filteredOptions.length && (
          <Grid className={classes.noOptionsMessage} container justifyContent="center">
            <Typography variant="caption">
              {!!minimumSearchCharacters && searchFilter.length < minimumSearchCharacters
                ? `Search term must be ${minimumSearchCharacters} characters or more.`
                : noOptionsMessage}
            </Typography>
          </Grid>
        )}
      </>
    )
  }

  const EndAdornment: React.FC<{ ownedByViewAdornment?: boolean }> = ({ ownedByViewAdornment }) => {
    // In normal (input) mode the chevron toggles the menu itself and stops
    // propagation. Previously it only ran handleClickAway (a no-op while closed),
    // so opening relied on the click bubbling to the input and on the document
    // ClickAwayListener not racing the open re-render — which left the closed
    // chevron effectively dead on some devices/desktop (#20209). ownedByView
    // keeps delegating the toggle to its parent container's onClick.
    const handleIconClick = ownedByViewAdornment
      ? handleClickAway
      : (e: React.MouseEvent) => {
          e.stopPropagation()
          if (anchorEl) {
            handleClickAway()
          } else if (!disabled && !loading) {
            setAnchorEl(selectRef.current)
          }
        }

    if (hideIcon) {
      return null
    } else if (anchorEl && !disabled && !loading) {
      return (
        <ExpandLessIcon
          className={classNames({
            [classes.icon]: !ownedByViewAdornment,
            [classes.ownedByViewIcon]: ownedByViewAdornment
          })}
          onClick={handleIconClick}
        />
      )
    } else if (loading) {
      return <Spinner size={20} />
    } else if (hasUnselect && hasValue && !disabled) {
      return (
        <ClearIcon
          className={classNames({
            [classes.icon]: !ownedByViewAdornment,
            [classes.ownedByViewIcon]: ownedByViewAdornment
          })}
          onClick={handleUnselect}
        />
      )
    } else {
      return (
        <ExpandMoreIcon
          className={classNames({
            [classes.icon]: !ownedByViewAdornment,
            [classes.ownedByViewIcon]: ownedByViewAdornment
          })}
          onClick={handleIconClick}
        />
      )
    }
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <FormControl fullWidth className={classes.root}>
        {label &&
          (toolTip ? (
            <>
              <div className={classes.toolTipWrapper}>
                <Tooltip
                  interactive={typeof toolTip !== 'string'}
                  content={
                    typeof toolTip === 'string' ? <span dangerouslySetInnerHTML={{ __html: toolTip }} /> : toolTip
                  }
                >
                  <Typography className={classes.tooltipTypography} variant="overline">
                    {label}
                    {inputProps.required && <span style={{ color: 'red' }}> *</span>}
                  </Typography>
                </Tooltip>
              </div>
              {sublabel && <InputLabel variant="sublabel">{sublabel}</InputLabel>}
            </>
          ) : (
            <>
              <InputLabel required={inputProps.required} className={sublabel ? null : classes.inputLabel} shrink>
                {label}
              </InputLabel>
              {sublabel && <InputLabel variant="sublabel">{sublabel}</InputLabel>}
            </>
          ))}
        {ownedByView ? (
          loading ? (
            <Skeleton width="240px" height="25px" />
          ) : (
            <div
              className={classes.ownedDropdown}
              onClick={(e) => {
                if (!anchorEl && !disabled && !loading) {
                  setAnchorEl(e.currentTarget)
                } else {
                  setAnchorEl(null)
                }
              }}
            >
              {ownedByUrl ? (
                <img className={classes.profileImage} src={ownedByUrl} />
              ) : (
                <Avatar
                  backgroundColor="#FFFFFF"
                  disableTooltip
                  filled
                  noTransform
                  className={classes.profileImage}
                  avatarColor={ownedByColor}
                  width="28.8px"
                  height="28.8px"
                  style={{
                    fontSize: '12px',
                    paddingLeft: 0,
                    paddingTop: 0
                  }}
                >
                  {/* Limit the username to two words so we only get two initials */}
                  {ownedByName.toUpperCase().split(/\s+/).slice(0, 2).join(' ')}
                </Avatar>
              )}
              {customOwnedByLabel ? (
                customOwnedByLabel
              ) : (
                <Typography variant="body1" color="textSecondary">
                  {ownedByName ? (
                    <>
                      Owned by <strong>{ownedByName}</strong>
                    </>
                  ) : (
                    <>Select User</>
                  )}
                </Typography>
              )}
              <div className={classes.ownedByViewAdornment}>
                <EndAdornment ownedByViewAdornment={ownedByView} />
              </div>
            </div>
          )
        ) : dealView ? (
          loading ? (
            <Skeleton width="240px" height="25px" />
          ) : (
            <div className={classes.dealViewContainer}>
              <Typography variant="subtitle2">{customOwnedByLabel ? customOwnedByLabel : 'Sales Rep:'}</Typography>
              <div
                className={classes.ownedDropdown}
                onClick={(e) => {
                  if (!anchorEl && !disabled && !loading) {
                    setAnchorEl(e.currentTarget)
                  } else {
                    setAnchorEl(null)
                  }
                }}
              >
                {ownedByUrl ? (
                  <img className={classes.profileImage} src={ownedByUrl} />
                ) : (
                  <Avatar
                    backgroundColor="#FFFFFF"
                    disableTooltip
                    filled
                    noTransform
                    className={classes.profileImage}
                    avatarColor={ownedByColor}
                    width="28.8px"
                    height="28.8px"
                    style={{
                      fontSize: '12px',
                      paddingLeft: 0,
                      paddingTop: 0
                    }}
                  >
                    {/* Limit the username to two words so we only get two initials */}
                    {ownedByName.toUpperCase().split(/\s+/).slice(0, 2).join(' ')}
                  </Avatar>
                )}
              </div>
            </div>
          )
        ) : (
          <InputField
            {...inputProps}
            endAdornment={inputProps.endAdornment ?? <EndAdornment />}
            disabled={disabled}
            classes={{
              root: classNames(classes.selectInputRoot, anchorEl ? classes.openSelectInput : null),

              input: classes.selectInput,
              focused: nestedDropdown ? classes.hiddenFocus : null,
              adornment: classes.adornment
            }}
            value={getDisplayValue()}
            placeholder={loading ? 'Loading...' : placeholder?.selectPlaceholder || 'Select'}
            inputRef={selectRef}
            fullWidth
            readOnly
            inputProps={{ ...(inputProps as any).inputProps, inputMode: 'none' }}
            onMouseDown={(e) => {
              e.preventDefault()
            }}
            onClick={(e) => {
              if (!anchorEl && !disabled && !loading) {
                setAnchorEl(e.currentTarget)
              }
            }}
            helperText={
              formatOptionLabel && value ? (
                <div className={classes.helperText}>{formatOptionLabel(value as DropdownSelectOption, 'selected')}</div>
              ) : (
                ''
              )
            }
          />
        )}
        <Popper
          open={!!anchorEl && !disabled}
          className={classes.popper}
          anchorEl={anchorEl}
          placement={popperPlacement}
          disablePortal={disablePortal}
          modifiers={popperModifiers}
        >
          <Paper
            id={'dropdown-menu-paper'}
            className={classes.paper}
            style={{ width: selectRef?.current?.clientWidth + (mobile ? 42 : 36) }}
          >
            {shouldShowHeaderActions && (
              <div className={classes.header}>
                <ClearIcon
                  className={classes.closeButton}
                  onClick={() => {
                    setAnchorEl(null)
                  }}
                />
              </div>
            )}
            {searchable && !notOnDefaultPage && (
              <DropdownSelectSearchBar
                loading={loading}
                value={searchFilter}
                placeholder={placeholder?.plural ? `Search ${placeholder.plural}` : 'Search'}
                onChange={handleSearchFilterChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !!searchFilter.trim() && !!onCreateOption) {
                    onCreateClick()
                  }
                }}
              />
            )}
            <Grid container className={classes.menuItemsContainer}>
              {MenuContent()}
            </Grid>
            {shouldShowFooter && (
              <DropdownSelectFooter
                clearLabel={notOnDefaultPage ? 'Back' : 'Clear'}
                onApply={() => handleApply({ allowCreateOption: true })}
                onClear={handleClear}
                hideDoneButton={hideDoneButton}
              />
            )}
          </Paper>
        </Popper>
        {showCollapsibleChips && multi && (selectedOptionsAreOverflowing() || allSelected || hasDeletedChips) && (
          <Collapse show={value && (value as DropdownSelectOption[]).length > 0}>
            <DropdownChips
              options={value as DropdownSelectOption[]}
              handleDelete={handleChipDelete}
              autoOpen={autoChipsOpen}
            />
          </Collapse>
        )}
      </FormControl>
    </ClickAwayListener>
  )
}
